'use client';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useAuthModal } from '@/contexts/auth-modal-context';
import { useAuth } from '@/hooks/useAuth';
import { LogIn, UserPlus } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';

export default function AuthModal() {
  const { isOpen, closeModal, redirectUrl } = useAuthModal();
  const { isAuthenticated } = useAuth();
  const t = useTranslations('AnonUserPopup');
  const tHeader = useTranslations('Header');
  const router = useRouter();
  const locale = useLocale();

  // Close modal if user just authenticated
  if (isAuthenticated && isOpen) {
    closeModal();
    return null;
  }

  const signInSlug = locale === 'sk' ? 'prihlasenie' : 'sign-in';
  const signUpSlug = locale === 'sk' ? 'registracia' : 'sign-up';

  const handleSignIn = () => {
    closeModal();
    const redirectParam = redirectUrl
      ? `?redirect=${encodeURIComponent(redirectUrl)}`
      : '';
    router.push(`/${locale}/${signInSlug}${redirectParam}`);
  };

  const handleSignUp = () => {
    closeModal();
    const redirectParam = redirectUrl
      ? `?redirect=${encodeURIComponent(redirectUrl)}`
      : '';
    router.push(`/${locale}/${signUpSlug}${redirectParam}`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeModal} data-testid='sign-in-modal'>
      <DialogContent
        className='max-w-[400px] p-8'
        data-testid='sign-in-modal-content'
      >
        <DialogTitle className='sr-only'>{t('welcome')}</DialogTitle>
        <div className='flex flex-col items-center text-center space-y-6'>
          <div>
            <h2 className='text-2xl font-semibold text-content-primary mb-2'>
              {t('welcome')}
            </h2>
            <p className='text-sm text-content-secondary'>
              {tHeader('singInOrSignUp')}
            </p>
          </div>

          <div className='w-full space-y-3'>
            {/* Sign Up Button */}
            <Button
              variant='brand'
              className='w-full h-11 text-sm group gap-2'
              onClick={handleSignUp}
            >
              <UserPlus className='h-4 w-4 transition-transform duration-300 group-hover:translate-y-[-2px]' />
              {tHeader('signUp')}
            </Button>

            {/* Sign In Button */}
            <Button
              variant='brandOutline'
              className='group gap-2 w-full h-11'
              onClick={handleSignIn}
            >
              <LogIn className='h-4 w-4 transition-transform duration-300 group-hover:translate-x-[-2px]' />
              <span className='font-medium text-sm'>{tHeader('signIn')}</span>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
